import { PuxWysiwygHtmlBodyType } from 'Shared/components/PuxWysiwyg/PuxWysiwyg'
import { getIsoCodesWithPrefixPublic } from 'Shared/utils/localeURL'

import { PricingPlanId } from './pricingPlans'
import { PricingCurrencyType, PricingFeatureType } from './pricingTypes'

type PricingFeaturePlanValue = boolean | PuxWysiwygHtmlBodyType | undefined

const LEGACY_BUTTON_CLASSES: Record<string, string> = {
  'Button--large': `Button--lg`,
  'Button--link': `Button--underline`,
  'Button--medium': `Button--md`,
  'Button--small': `Button--sm`,
  'Button--white': ``,
}

export interface PricingFormattedPrice {
  currency: string[]
  currencyLast: boolean
  fraction: string[]
  integer: string[]
}

export const getPricingIsoCode = (project: string, locale?: string): string =>
  getIsoCodesWithPrefixPublic(false, project).filter(
    (iso) => iso.locale === locale
  )[0]?.isoCode ?? `en-GB`

export const getCurrencyPrice = (
  currency: PricingCurrencyType,
  plan: PricingPlanId
): number =>
  Number(
    ((currency as unknown) as Record<string, string>)[
      `pricingCurrencyPrice${plan}`
    ]
  )

export const formatCurrencyPrice = (
  currency: PricingCurrencyType,
  plan: PricingPlanId,
  isoCode: string
): PricingFormattedPrice => {
  const number = new Intl.NumberFormat(isoCode, {
    style: `currency`,
    currency: currency.displayText.toUpperCase(),
  }).formatToParts(getCurrencyPrice(currency, plan))

  const integer: string[] = []
  const fraction: string[] = []
  const formattedCurrency: string[] = []
  let currencyLast = true

  number.forEach((part, idx) => {
    if (part.type === `integer` || part.type === `group`) {
      integer.push(part.value)
    }

    if (part.type === `fraction` && part.value !== `00`) {
      fraction.push(part.value)
    }

    if (part.type === `literal` || part.type === `currency`) {
      if (part.type === `currency` && idx === 0) {
        currencyLast = false
      }
      formattedCurrency.push(part.value.replace(`US`, ``))
    }
  })

  return {
    currency: formattedCurrency,
    currencyLast,
    fraction,
    integer,
  }
}

export const getDefaultCurrencyIndex = (
  currencies: PricingCurrencyType[],
  cookieValue?: string
): number => {
  if (!cookieValue) return 0

  const idx = currencies.findIndex((currency) =>
    currency.pricingCurrencyDefaultInCountriesList?.includes(cookieValue)
  )

  return idx >= 0 ? idx : 0
}

const getFeatureField = (
  feature: PricingFeatureType,
  field: string
): PricingFeaturePlanValue =>
  ((feature as unknown) as Record<string, PricingFeaturePlanValue>)[field]

export const isFeatureAvailable = (
  feature: PricingFeatureType,
  plan: PricingPlanId
): boolean => getFeatureField(feature, `pricingFeaturePlan${plan}`) === true

export const getFeatureInputHtml = (
  feature: PricingFeatureType,
  plan: PricingPlanId
): string | undefined => {
  const input = getFeatureField(feature, `pricingFeaturePlan${plan}Input`)

  return typeof input === `object` && input
    ? normalizePricingButtonClasses(input.html)
    : undefined
}

const normalizePricingButtonClasses = (html: string): string =>
  html.replace(/class=(['"])(.*?)\1/g, (_match, quote, classNames) => {
    const normalizedClasses = classNames
      .split(/\s+/)
      .map((className) => LEGACY_BUTTON_CLASSES[className] ?? className)
      .filter(Boolean)

    return `class=${quote}${Array.from(new Set(normalizedClasses)).join(
      ` `
    )}${quote}`
  })
